/**
 * Camera line-of-sight VIEWSHED — pure terrain LOS math (#178 Cesium-wow).
 *
 * The honest, terrain-real upgrade to the flat FOV cones: given a camera origin, a
 * bounded radius, and a way to sample terrain height along the ground, compute the
 * ground area the camera can actually SEE — accounting for terrain occlusion ("does
 * this camera actually see the fire over that ridge?"). It directly backs the
 * cameraSmoke E.W.4 fusion (PRD §4.16), which correlates a camera to a hotspot
 * within a 10 km smoke-LOS radius + ±30° bearing but does NOT yet check terrain
 * occlusion — this makes that correlation visible and defensible.
 *
 * This module is PURE + dependency-free (NO Cesium import) so the LOS math is
 * unit-testable with synthetic terrain (flat = all visible; a ridge between =
 * occluded beyond the ridge; downhill = visible). The Cesium integration
 * (CameraViewshedLayer) supplies the real sampled terrain heights and drapes the
 * result; mirrors how floodScenario.ts holds the unit-tested flood math.
 *
 * ── The LOS algorithm (per radial ray, samples ordered near → far) ──
 * Walk outward from the camera EYE (origin terrain height + a documented mount
 * height). Track the running MAXIMUM elevation-angle (as a tangent: rise/run) of all
 * closer terrain. A ground sample is VISIBLE iff its own elevation angle from the eye
 * clears that running maximum — i.e. no nearer terrain rises above the straight
 * sight-line to it. A ridge sets a high running-max, throwing an occlusion shadow on
 * the lower ground beyond it. (Flat-earth local approximation — earth curvature is
 * ignored over the ≤10 km radius, exactly as floodInundation ignores curvature over
 * its ~10 km ENU plane. v1 also models terrain only, not vegetation/buildings.)
 */

/**
 * Assumed camera mount height above local ground (m). The viewshed serves the
 * smoke-priority cameras — fire-detection towers, rooftop/pole panoramas, mountain
 * landscape cams — which are deliberately mounted high to see far. Without a
 * per-camera surveyed mast height we assume one conservative value.
 *
 * Phase 0 finding (#178, Grouse Mountain spike): the eye height is the single most
 * load-bearing assumption. A 4 m eye on a broad ~flat summit produces a near-level
 * local horizon that hides the lower ground beyond it (geometrically correct, but a
 * tiny, uninformative footprint). 15 m matches the real tower/rooftop mounts of these
 * elevated cameras and yields a meaningful, terrain-shadowed viewshed. This is a
 * DOCUMENTED modelling assumption (the layer legend names it), NOT a survey value —
 * flagged for the founder as a deviation from the suggested 4 m default, with the
 * measured rationale above.
 */
export const MOUNT_HEIGHT_M = 15;

/** Earth mean radius (m) for the great-circle destination formula. */
const EARTH_RADIUS_M = 6_371_008.8;

/** Grazing tolerance on the visibility comparison (radians-equivalent tangent): a
 *  sample exactly on the running-max sight-line reads as visible, not occluded. */
const GRAZE_EPS = 1e-9;

export interface LonLat {
  lon: number;
  lat: number;
}

/** One ground sample along a ray, at a known along-ground distance from the camera. */
export interface RaySample extends LonLat {
  /** Along-ground distance from the camera origin (m). */
  distM: number;
}

/** A ground sample paired with its sampled terrain height (ellipsoid metres). */
export interface SampledRayPoint {
  distM: number;
  terrainHeightM: number;
}

/** Per-ray visibility result. */
export interface RayVisibility {
  /** Strict per-sample visibility: true where the sample clears all closer terrain
   *  (so a re-emerging hilltop beyond a ridge can read visible again). */
  visible: boolean[];
  /** Farthest CONTIGUOUS visible distance from the camera (m) — stops at the first
   *  occlusion. This is what the draped footprint polygon uses (the clean "what the
   *  camera sees in front of it" reach), 0 when the nearest sample is already blocked. */
  reachM: number;
  /** Distance of the first occluded sample (m), or null if the whole ray is visible. */
  firstOccludedDistM: number | null;
}

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const toDeg = (rad: number): number => (rad * 180) / Math.PI;

/**
 * Great-circle destination point: start at `origin`, travel `distanceM` along bearing
 * `bearingDeg` (degrees clockwise from true north). Pure spherical-earth formula —
 * accurate to well under a metre over the ≤10 km radii this layer uses.
 */
export function destinationPoint(
  origin: LonLat,
  bearingDeg: number,
  distanceM: number,
): LonLat {
  const δ = distanceM / EARTH_RADIUS_M; // angular distance
  const θ = toRad(bearingDeg);
  const φ1 = toRad(origin.lat);
  const λ1 = toRad(origin.lon);

  const sinφ1 = Math.sin(φ1);
  const cosφ1 = Math.cos(φ1);
  const sinδ = Math.sin(δ);
  const cosδ = Math.cos(δ);

  const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const y = Math.sin(θ) * sinδ * cosφ1;
  const x = cosδ - sinφ1 * sinφ2;
  const λ2 = λ1 + Math.atan2(y, x);

  // Normalise longitude to [-180, 180].
  let lon = toDeg(λ2);
  lon = ((lon + 540) % 360) - 180;
  return { lon, lat: toDeg(φ2) };
}

/**
 * The list of azimuths (deg from north) to sweep. Directional cameras (a known
 * heading) sweep only the FOV wedge plus a small margin; heading-unknown or PTZ
 * cameras sweep the full 360°. Pure — no Cesium.
 */
export function sweepAzimuths(opts: {
  /** Camera aim (deg from north); null/undefined → omnidirectional 360° sweep. */
  headingDeg: number | null | undefined;
  /** Field of view (deg) — only used when headingDeg is known. */
  fovDeg: number;
  /** Angular resolution of the sweep (deg between rays). */
  stepDeg: number;
  /** Extra wedge margin each side of the FOV (deg) so the visible edge isn't clipped. */
  marginDeg?: number;
}): number[] {
  const { headingDeg, fovDeg, stepDeg, marginDeg = 5 } = opts;
  const step = Math.max(0.25, stepDeg);
  const out: number[] = [];

  const directional =
    headingDeg !== null &&
    headingDeg !== undefined &&
    Number.isFinite(headingDeg) &&
    Number.isFinite(fovDeg) &&
    fovDeg > 0 &&
    fovDeg < 360;

  if (!directional) {
    // Full 360° sweep (exclusive of the duplicate 360 == 0 ray).
    for (let a = 0; a < 360 - 1e-6; a += step) out.push(((a % 360) + 360) % 360);
    return out;
  }

  const half = fovDeg / 2 + marginDeg;
  for (let off = -half; off <= half + 1e-6; off += step) {
    out.push(((headingDeg! + off) % 360 + 360) % 360);
  }
  return out;
}

/**
 * Build a radial sample grid around the camera: for each azimuth, ground samples at
 * `rangeStepM` intervals from the first step out to `maxRangeM` (inclusive). Returns
 * one ray (array of samples, near → far) per azimuth. Bounded — no sample exceeds
 * maxRangeM, so the integration layer never streams terrain beyond the camera radius.
 */
export function buildRadialGrid(
  origin: LonLat,
  azimuthsDeg: number[],
  rangeStepM: number,
  maxRangeM: number,
): RaySample[][] {
  const step = Math.max(1, rangeStepM);
  const rays: RaySample[][] = [];
  for (const az of azimuthsDeg) {
    const ray: RaySample[] = [];
    for (let d = step; d <= maxRangeM + 1e-6; d += step) {
      const p = destinationPoint(origin, az, d);
      ray.push({ lon: p.lon, lat: p.lat, distM: d });
    }
    rays.push(ray);
  }
  return rays;
}

/**
 * Core LOS test for one ray. `eyeHeightM` is the camera eye ellipsoid height (origin
 * terrain height + MOUNT_HEIGHT_M). `samples` are the ground samples (near → far) with
 * their sampled terrain heights. Returns per-sample visibility, the contiguous visible
 * reach, and the first occlusion distance.
 */
export function rayVisibility(
  eyeHeightM: number,
  samples: SampledRayPoint[],
): RayVisibility {
  const visible: boolean[] = [];
  let maxTan = -Infinity; // running max elevation angle (rise/run) of closer terrain
  let reachM = 0;
  let firstOccludedDistM: number | null = null;
  let contiguous = true;

  for (const s of samples) {
    const d = s.distM > 0 ? s.distM : 1e-6;
    const tan = (s.terrainHeightM - eyeHeightM) / d;
    const vis = tan + GRAZE_EPS >= maxTan;
    visible.push(vis);

    if (contiguous) {
      if (vis) {
        reachM = s.distM;
      } else {
        contiguous = false;
        firstOccludedDistM = s.distM;
      }
    } else if (!vis && firstOccludedDistM === null) {
      firstOccludedDistM = s.distM;
    }

    if (tan > maxTan) maxTan = tan;
  }

  return { visible, reachM, firstOccludedDistM };
}
